import React, { useEffect, useMemo, useState } from 'react'

import Marquee from 'react-fast-marquee'

import SocialProofItem from './components/SocialProofItem.component'

import { useSocialProofItems } from '../../hooks/useSocialProofItems'
import styles from './SocialProofBanner.styles'
import { SocialProofBannerProps } from './SocialProofBanner.types'

const SocialProofBanner: React.FC<SocialProofBannerProps> = ({ items: initialItems = [] }) => {
  const { items, loading } = useSocialProofItems(initialItems)
  const [screenWidth, setScreenWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200)

  // Track screen width changes
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth)
    }

    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Calculate dynamic spacing and item duplication
  const { itemsToRender, dynamicSpacing } = useMemo(() => {
    if (items.length === 0) {
      return { itemsToRender: [], dynamicSpacing: 90 }
    }

    // Actual item width based on SocialProofItem styles (120px width + padding)
    const estimatedItemWidth = 120 + 16 // 120px width + 8px padding on each side
    const baseSpacing = 90

    // Calculate how many items fit in one screen width
    const itemsPerScreen = Math.floor(screenWidth / (estimatedItemWidth + baseSpacing))

    // Calculate optimal spacing to fill the screen
    const availableSpaceForSpacing = screenWidth - itemsPerScreen * estimatedItemWidth
    const optimalSpacing = Math.max(baseSpacing, availableSpaceForSpacing / itemsPerScreen)

    // Duplicate items if needed to ensure smooth scrolling without gaps
    const minItemsNeeded = Math.max(itemsPerScreen * 2, items.length)
    let duplicatedItems = [...items]

    while (duplicatedItems.length < minItemsNeeded) {
      duplicatedItems = [...duplicatedItems, ...items]
    }

    return {
      itemsToRender: duplicatedItems,
      dynamicSpacing: optimalSpacing
    }
  }, [items, screenWidth])

  // Don't render if no items and not loading
  if (items.length === 0 && !loading) {
    return null
  }

  // Create dynamic styles
  const dynamicItemStyle = {
    ...styles.itemContainer,
    marginRight: `${dynamicSpacing}px`
  }

  return (
    <div role="banner" aria-label="Social proof testimonials" style={styles.container}>
      <div style={styles.titleContainer}>
        <h3 style={styles.title}>As Seen On</h3>
      </div>
      <Marquee
        pauseOnHover={true}
        speed={50}
        gradient={true}
        gradientColor="#ffffff"
        gradientWidth={60}
        style={styles.marqueeContainer}
      >
        {itemsToRender.map((item, index) => {
          // Generate unique keys for each item (including duplicates)
          const uniqueKey = `${item.imageUrl}-${index}-${Math.floor(index / items.length)}`

          return (
            <a
              key={uniqueKey}
              href={item.linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={item.altText}
              style={dynamicItemStyle}
            >
              <SocialProofItem item={item} />
            </a>
          )
        })}
      </Marquee>
    </div>
  )
}

export default SocialProofBanner
